import * as THREE from 'three'
import {useEffect, useRef,} from 'react'
import {useControls} from 'leva'
import {useFrame} from '@react-three/fiber'
import {useRoomTextures} from '../hooks/useRoomTextures.jsx'
import {useRoomModels} from '../hooks/useRoomModels.jsx'
import {useRoomMaterials} from '../hooks/useRoomMaterials.jsx'
import {getDayNightMixValue} from '../utils/timeCalculation.jsx'
import {RoomFigurines} from './RoomFigurines.jsx'
import {ComputerScreen} from './ComputerScreen.jsx'
import {TrophyModel} from './TrophyModel.jsx'
import {TabletModel} from './TabletModel.jsx'
import {useAppStore} from '../stores/store.js'
import {useGameStore} from '../stores/gameStore.js'
import {LinkedInModel} from './LinkedInModel.jsx'
import {TargetModel} from './TargetModel.jsx'
import {GameTargets} from './GameTargets.jsx'
import {StrawberryConsole} from './strawberryConsole.jsx'

// import {useRoomDayNightControls} from '../hooks/useRoomControls.jsx'


export function Room({...props}) {

    const isSecretObjectVisible = useAppStore((state) => state.isSecretObjectVisible)
    const gameState = useGameStore((state) => state.gameState)

    // useEffect(() => {
    //     console.log('isSecretObjectVisible:', isSecretObjectVisible)
    // }, [isSecretObjectVisible])

    // References
    const roomMaterialRef = useRef(null)
    const controlValuesRef = useRef({
        useRealTime: true,
        dayTimeMix: 0,
        // lastCalculationTime: Date.now()
    })
    const timeCalculationRef = useRef({
        lastCalculated: 0,
        mixValue: 0
    })
    // const trophyRef = useRef(null)
    // const tabletRef = useRef(null)

    // Load textures and models
    const {roomDayTexture, roomNightTexture, booksTexture} = useRoomTextures()
    const {
        roomNode,
        booksNode,
        transparentNodes,
        figurineNodes,
        transparentBox,
        transparentWindow,
        customFigurineMaterials,
    } = useRoomModels()

    // Initial calculation
    const initialDayNightMix = getDayNightMixValue(timeCalculationRef)

    // DAY NIGHT CONTROLS Leva Controls
    const {dayTimeMix, useRealTime} = useControls('Room', {
        dayTimeMix: {
            value: initialDayNightMix,
            min: 0,
            max: 1,
            step: 0.01,
            onChange: (value) => controlValuesRef.current.dayTimeMix = value
        },
        useRealTime: {
            value: true,
            onChange: (value) => controlValuesRef.current.useRealTime = value
        }
    })

    const stripeControls = useControls('Window Blinds', {
        stripeCount: {value: 2, min: 1, max: 5, step: 1},
        stripeRatio: {value: 0.5, min: 0.1, max: 0.9, step: 0.05},
        offset: {value: 0.2, min: 0, max: 1, step: 0.01},
        opacity: {value: 0.5, min: 0, max: 1, step: 0.01},
        color: {value: '#ffffff'},
    }, {collapsed: true})

    // Create materials
    const {roomMaterial, stripeMaterial, musicMaterial} = useRoomMaterials(
        roomDayTexture,
        roomNightTexture,
        initialDayNightMix,
        stripeControls
    )

    useEffect(() => {
        roomMaterialRef.current = roomMaterial
    }, [roomMaterial])

    // Room Material Update 
    useEffect(() => {
        if (controlValuesRef.current.useRealTime) {
            roomMaterialRef.current.uniforms.uDayTimeMix.value = getDayNightMixValue(timeCalculationRef)
        }

        const intervalId = setInterval(() => {
            if (controlValuesRef.current.useRealTime) {
                roomMaterialRef.current.uniforms.uDayTimeMix.value = getDayNightMixValue(timeCalculationRef)
                // controlValuesRef.current.lastCalculationTime = Date.now()
            }
        }, 1000) // Update every second

        return () => clearInterval(intervalId)
    }, [])

    // Handle manual control updates in animation frame
    useFrame(() => {
        if (!controlValuesRef.current.useRealTime) {
            roomMaterialRef.current.uniforms.uDayTimeMix.value = controlValuesRef.current.dayTimeMix
        }
    })

    // Update material in animation frame
    /*useFrame(() => {
        // if (!roomMaterialRef.current) return
        if (controlValuesRef.current.useRealTime) {
            // roomMaterial.uniforms.uDayTimeMix.value = getDayNightMixValue(timeCalculationRef)
            roomMaterialRef.current.uniforms.uDayTimeMix.value = getDayNightMixValue(timeCalculationRef)
        } else {
            // roomMaterial.uniforms.uDayTimeMix.value = controlValuesRef.current.dayTimeMix
            roomMaterialRef.current.uniforms.uDayTimeMix.value = controlValuesRef.current.dayTimeMix
        }
    })*/

    // Set scene ready for launch animation
    useEffect(() => {
        useAppStore.getState().setSceneReady()
    }, [])

    return <>
        {/* Room */}
        <mesh {...props}
              name='room'
              geometry={roomNode.room.geometry}
              material={roomMaterial}
              dispose={null}
              onPointerOver={(e) => {
                  e.stopPropagation()
              }}
        />

        {/* Books */}
        <mesh
            {...props}
            name='books'
            geometry={booksNode.books.geometry}
            dispose={null}
        >
            <meshBasicMaterial
                map={booksTexture}
                map-flipY={false}
                side={THREE.DoubleSide}
            />
        </mesh>

        {/* MUSIC TEST PLANE*/}
        {/*<mesh
            material={musicMaterial}
            scale={[10, 10, 1]}
            position={[0, 5, -10]}
            rotation={[0, Math.PI, 0]}
        >
            <planeGeometry args={[1, 1]}/>
        </mesh>*/}

        {/* Trophy Model */}
        <TrophyModel {...props}/>
        <TabletModel  {...props}/>
        <LinkedInModel {...props}/>
        <TargetModel {...props}/>
        <StrawberryConsole {...props}/>
        {isSecretObjectVisible && (
            <></>
        )}
        {gameState === 'playing' && (
            <GameTargets/>
        )}


        {/* Computer Screen */}
        <ComputerScreen/>

        {/* Transparent Objects */}
        <group
            {...props}
            dispose={null}
            name='transparent_meshes'
        >
            <mesh
                name='transparent_boxes'
                geometry={transparentNodes.transparent_1.geometry}
                material={transparentBox}
            />
            <mesh
                name='transparent_window'
                geometry={transparentNodes.transparent_2.geometry}
                material={transparentWindow}
            />
            <mesh
                name='transparent_stores'
                geometry={transparentNodes.transparent_3.geometry}
                material={stripeMaterial}
            />
        </group>

        {/* Figurines */}
        <RoomFigurines
            {...props}
            nodes={figurineNodes}
            materials={customFigurineMaterials}
            dispose={null}
        />
    </>
}